<!--
  QuickActions.svelte

  Renders quick action buttons/chips for chat messages.
  Includes retry button for errors and "Explain More" button.
-->
<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Chip, ButtonV2 } from '@Shelly/shelly-design-system';
	import type { QuickAction } from '../../services/ai-scene-chat-api';

	const CHIP_TYPES: Set<string> = new Set(['room', 'device', 'timing', 'group', 'scene', 'parameter']);

	const CHIP_VARIANT_MAP: Record<string, string> = {
		room: 'secondary',
		device: 'primary',
		timing: 'secondary',
		group: 'secondary',
		scene: 'success',
		parameter: 'primary',
	};

	const CHIP_ICON_MAP: Record<string, string> = {
		room: 'fas fa-door-open',
		device: 'fas fa-plug',
		timing: 'fas fa-clock',
		group: 'fas fa-layer-group',
		scene: 'fas fa-film',
		parameter: 'fas fa-sliders-h',
	};

	function chipVariant(type?: string): string {
		return CHIP_VARIANT_MAP[type || ''] || 'secondary';
	}

	function chipIcon(action: { icon?: string; type?: string }): string | undefined {
		return action.icon || CHIP_ICON_MAP[action.type || ''];
	}

	function isChipType(type?: string): boolean {
		return CHIP_TYPES.has(type || '');
	}

	export let quickActions: QuickAction[] | undefined;
	export let isErrorMessage: boolean;
	export let showExplainMore: boolean;
	export let onQuickAction: ((message: string) => void) | undefined;
	export let onRetry: (() => void) | undefined;
	export let onExplainMore: (() => void) | undefined;

	$: hasQuickActions = (quickActions?.length ?? 0) > 0;
</script>

{#if hasQuickActions && onQuickAction}
	<div class="quick-actions">
		{#each quickActions as action (action.id)}
			{#if isChipType(action.type)}
				<button class="option-chip-btn" on:click={() => onQuickAction(action.message)}>
					<Chip
						variant={chipVariant(action.type)}
						icon={chipIcon(action)}
						class="mr-xs mb-xs"
						aria-label={action.label}
					>
						{action.label}
					</Chip>
				</button>
			{:else}
				<ButtonV2
					variant="primary"
					size="s"
					icon={'fa-thin fa-sparkles'}
					on:click={() => onQuickAction(action.message)}
				>
					{action.label}
				</ButtonV2>
			{/if}
		{/each}
	</div>
{:else if isErrorMessage && onRetry}
	<div class="quick-actions">
		<ButtonV2 variant="primary" size="m" on:click={onRetry}>
			{$format('button_retry', { default: 'Retry' })}
		</ButtonV2>
	</div>
{/if}

{#if showExplainMore && onExplainMore}
	<div class="quick-actions">
		<ButtonV2 variant="primary" size="s" icon="fas fa-question-circle" on:click={onExplainMore}>
			{$format('button_explain_more', { default: 'Explain More' })}
		</ButtonV2>
	</div>
{/if}

<style>
	.quick-actions {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		margin-top: 12px;
	}

	.option-chip-btn {
		all: unset;
		cursor: pointer;
		display: inline-flex;
	}

	.option-chip-btn:hover {
		opacity: 0.85;
	}

	.option-chip-btn:active {
		transform: scale(0.96);
	}
</style>
